import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { SERVICE_ICONS } from "@/data/static";
import { SEO_SERVICES, SEO_SERVICE_SLUGS } from "@/data/seoServices";
import { ArrowRight, Building2, Sparkles } from "lucide-react";

export default function ServicesOverview() {
  const { t } = useLang();
  const services = ["fm", "cleaning", "maintenance", "manpower", "snagging", "projects"];

  return (
    <div data-testid="services-page">
      <SEO
        title={`Services — Facility Management, Cleaning, Maintenance & Manpower | ${COMPANY.brand}`}
        description="Integrated FM services in the UAE: facility management, cleaning, maintenance and manpower supply. SLA-driven, single-vendor convenience."
      />

      {/* HERO — dark cinematic to match Home */}
      <section className="relative overflow-hidden bg-[#050A15] text-white">
        <div className="absolute inset-0 pointer-events-none">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=90&w=2400"
            alt=""
            aria-hidden
            className="absolute inset-0 w-full h-full object-cover object-center opacity-75"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A15]/92 via-[#050A15]/55 to-[#050A15]/20" />
          <div className="absolute -top-40 -right-32 w-[420px] h-[420px] rounded-full bg-blue-600/15 blur-[140px]" />
        </div>
        <div className="container-orion relative py-24 lg:py-32">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300 border border-blue-400/30 rounded-full px-3 py-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            {t("services.sectionLabel")}
          </div>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-white max-w-4xl">
            {t("services.sectionTitle")}
          </h1>
          <p className="mt-6 text-lg text-slate-300/90 max-w-2xl leading-relaxed">
            {t("services.sectionDesc")}
          </p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32 bg-white">
        <div className="container-orion">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {services.map((k) => {
              const slug = t(`services.${k}.slug`);
              const Icon = SERVICE_ICONS[slug] || Building2;
              return (
                <Link
                  key={k}
                  to={`/services/${slug}`}
                  data-testid={`services-overview-card-${slug}`}
                  className="group relative bg-white border border-slate-200 rounded-2xl p-8 lg:p-10 card-hover overflow-hidden"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-14 h-14 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                      <Icon className="w-7 h-7" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-700 group-hover:translate-x-1 rtl-flip transition-all" />
                  </div>
                  <h2 className="mt-7 font-heading text-2xl lg:text-3xl tracking-tight font-medium text-slate-900">
                    {t(`services.${k}.title`)}
                  </h2>
                  <p className="mt-4 text-base text-slate-600 leading-relaxed">
                    {t(`services.${k}.long`)}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Specialized services — SEO landing pages */}
      <section className="py-24 lg:py-28 bg-slate-50">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">Specialized services</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              16 focused services across the UAE.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Pick the exact service you need — pricing, scope, FAQ and lead form for each.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SEO_SERVICE_SLUGS.map((slug) => {
              const svc = SEO_SERVICES[slug];
              return (
                <Link
                  key={slug}
                  to={`/services/${slug}`}
                  data-testid={`seo-service-card-${slug}`}
                  className="group bg-white border border-slate-200 rounded-2xl p-6 card-hover"
                >
                  <div className="flex items-center justify-between gap-2">
                    <Sparkles className="w-5 h-5 text-blue-700" />
                    <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-700 group-hover:translate-x-1 rtl-flip transition-all" />
                  </div>
                  <h3 className="mt-4 font-heading text-base font-medium text-slate-900 leading-snug">
                    {svc.h1.replace(" in UAE", "").replace(" Services in UAE", " Services")}
                  </h3>
                  <p className="mt-2 text-xs text-slate-500 leading-relaxed line-clamp-2">
                    {svc.intro}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW WE WORK — 4-step funnel */}
      <section className="py-24 lg:py-28 bg-[#050A15] text-white relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
          backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }} />
        <div className="container-orion relative">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-blue-300 flex items-center gap-3">
              <span className="w-8 h-px bg-blue-400" />
              How we work
            </div>
            <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium leading-tight">
              Four steps from enquiry to first shift.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "01", title: "Enquiry", desc: "Share your site + services on the form or WhatsApp. Response within 1 business hour." },
              { n: "02", title: "Site inspection", desc: "Free walkthrough by a senior consultant. We benchmark your current spend + SLAs." },
              { n: "03", title: "Fixed proposal", desc: "Written scope, monthly fee, SLA commitments and start date. No surprises." },
              { n: "04", title: "Onboarding", desc: "Uniformed team mobilises within 7 days. Dedicated supervisor + WhatsApp hotline live from day 1." },
            ].map((s) => (
              <div key={s.n} className="relative bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-blue-400/40 rounded-2xl p-7 transition-colors">
                <div className="font-heading text-4xl font-medium text-blue-300">{s.n}</div>
                <h3 className="mt-4 font-heading text-lg font-medium text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
